import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  alpha
} from '@mui/material';
import {
  CardMembership,
  CheckCircle,
  EmojiEvents,
  Star,
  LocalShipping,
  NewReleases,
  Person,
  School,
  Groups,
  Event,
  CardGiftcard,
  Discount,
  Verified,
  CalendarToday,
  Payment,
  Shield
} from '@mui/icons-material';
import SecuredLayout from '../components/layouts/SecuredLayout';
import userService from '../services/userService';

const Membership = () => {
  const theme = useTheme();
  const [memberships, setMemberships] = useState([]);
  const [userMemberships, setUserMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [successDialog, setSuccessDialog] = useState({ open: false, membership: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const formatPrice = (price) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `₹${numPrice.toFixed(2)}`;
  };

  const parseBenefits = (benefitsText) => {
    if (!benefitsText) return [];
    return benefitsText
      .split(/\n|•|,/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [membershipsRes, userMembershipsRes] = await Promise.all([
        userService.getMemberships(),
        userService.getUserMemberships()
      ]);
      setMemberships(membershipsRes.data.results || membershipsRes.data || []);
      setUserMemberships(userMembershipsRes.data.results || userMembershipsRes.data || []);
    } catch (err) {
      console.error('Error fetching membership data:', err);
      setError(err.response?.data?.detail || 'Failed to load membership data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const activeMembership = userMemberships.find((um) => um.is_active);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleSubscribe = async (plan) => {
    if (activeMembership && activeMembership.membership === plan.id) {
      setSnackbar({ open: true, message: 'You already have this active membership.', severity: 'info' });
      return;
    }

    setProcessingId(plan.id);
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setSnackbar({ open: true, message: 'Failed to load Razorpay. Please try again.', severity: 'error' });
        return;
      }

      const orderResponse = await userService.createMembershipRazorpayOrder({ membership_id: plan.id });
      const { razorpay_order_id, amount, currency, key_id, membership_id } = orderResponse.data;

      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: 'Pharmaman',
        description: `${plan.name} Membership`,
        order_id: razorpay_order_id,
        handler: async (response) => {
          try {
            const verifyResponse = await userService.verifyMembershipRazorpayPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              membership_id: membership_id
            });
            setSuccessDialog({ open: true, membership: verifyResponse.data });
            await fetchData();
            setSnackbar({
              open: true,
              message: 'Membership purchased successfully!',
              severity: 'success'
            });
          } catch (err) {
            console.error('Payment verification failed:', err);
            setSnackbar({
              open: true,
              message: err.response?.data?.error || 'Payment verification failed. Please contact support.',
              severity: 'error'
            });
          }
        },
        prefill: {
          name: '',
          email: '',
          contact: ''
        },
        theme: {
          color: theme.palette.primary.main
        },
        modal: {
          ondismiss: () => {
            setProcessingId(null);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      console.error('Error initiating membership payment:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to initiate payment. Please try again.',
        severity: 'error'
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const getPlanMeta = (plan) => {
    const name = plan.name.toLowerCase();
    if (name.includes('vip')) return { icon: <Shield />, color: theme.palette.secondary.dark };
    if (name.includes('premium')) return { icon: <EmojiEvents />, color: theme.palette.secondary.main };
    if (name.includes('enhanced')) return { icon: <Star />, color: theme.palette.info.main };
    return { icon: <CardMembership />, color: theme.palette.primary.main };
  };

  const sortedMemberships = [...memberships].sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
  const popularPlan = sortedMemberships.length > 1 ? sortedMemberships[sortedMemberships.length - 1] : null;

  const renderHero = () => (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        borderRadius: 4,
        p: { xs: 3, md: 5 },
        mb: 4,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -40,
          right: -40,
          width: 220,
          height: 220,
          borderRadius: '50%',
          backgroundColor: alpha('#ffffff', 0.08)
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -60,
          left: -60,
          width: 280,
          height: 280,
          borderRadius: '50%',
          backgroundColor: alpha('#ffffff', 0.05)
        }}
      />
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: alpha('#ffffff', 0.2), color: '#ffffff', mr: 2, width: 56, height: 56 }}>
            <EmojiEvents sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#ffffff' }}>
            PharmaMan Membership
          </Typography>
        </Box>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 500, color: alpha('#ffffff', 0.95) }}>
          Join our exclusive membership program and unlock premium benefits!
        </Typography>
        <Typography variant="body1" sx={{ color: alpha('#ffffff', 0.85), maxWidth: 700 }}>
          Enjoy cashback, free delivery, priority support, and early access to new products.
        </Typography>
      </Box>
    </Box>
  );

  const renderActiveMembership = () => {
    if (!activeMembership) return null;
    const endDate = new Date(activeMembership.end_date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const startDate = new Date(activeMembership.start_date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    return (
      <Card
        elevation={0}
        sx={{
          mb: 4,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'success.main',
          bgcolor: alpha(theme.palette.success.main, 0.06)
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
              <Verified sx={{ color: '#ffffff', fontSize: 32 }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.dark', mb: 0.5 }}>
                Active Membership: {activeMembership.membership_name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', color: 'text.secondary' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <CalendarToday fontSize="small" />
                  <Typography variant="body2">Started: {startDate}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <Event fontSize="small" />
                  <Typography variant="body2">Expires: {endDate}</Typography>
                </Box>
              </Box>
            </Box>
            <Chip label="Active" color="success" icon={<Verified />} sx={{ fontWeight: 600, borderRadius: 2 }} />
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderPlans = () => (
    <Box sx={{ mb: 6 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: 'text.primary' }}>
        Choose Your Plan
      </Typography>
      <Grid container spacing={3} alignItems="stretch">
        {sortedMemberships.map((plan) => {
          const meta = getPlanMeta(plan);
          const isPopular = popularPlan && plan.id === popularPlan.id;
          const isActive = activeMembership && activeMembership.membership === plan.id;
          const benefits = parseBenefits(plan.benefits);

          return (
            <Grid item xs={12} sm={6} md={4} key={plan.id}>
              <Card
                elevation={isActive ? 6 : 2}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: isActive ? 'success.main' : 'divider',
                  bgcolor: '#ffffff',
                  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: 6
                  }
                }}
              >
                <Box
                  sx={{
                    height: 6,
                    backgroundColor: meta.color
                  }}
                />
                <CardContent sx={{ p: 3, flex: 1 }}>
                  {isPopular && (
                    <Chip
                      label="Most Popular"
                      color="secondary"
                      size="small"
                      sx={{ mb: 2, fontWeight: 600, borderRadius: 1 }}
                    />
                  )}
                  {isActive && (
                    <Chip
                      label="Current Plan"
                      color="success"
                      size="small"
                      sx={{ mb: 2, fontWeight: 600, borderRadius: 1 }}
                    />
                  )}

                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Avatar
                      sx={{
                        width: 76,
                        height: 76,
                        mx: 'auto',
                        mb: 2,
                        bgcolor: alpha(meta.color, 0.12),
                        color: meta.color
                      }}
                    >
                      {React.cloneElement(meta.icon, { sx: { fontSize: 36 } })}
                    </Avatar>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                      {plan.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {plan.duration_months} months
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary' }}>
                        {formatPrice(plan.price)}
                      </Typography>
                      {plan.discount_percent > 0 && (
                        <Chip
                          label={`${plan.discount_percent}% off`}
                          color="success"
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      )}
                    </Box>
                  </Box>

                  <List dense>
                    {benefits.length > 0 ? (
                      benefits.map((benefit, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={benefit}
                            primaryTypographyProps={{ variant: 'body2', color: 'text.primary' }}
                          />
                        </ListItem>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        {plan.description || 'No benefits listed'}
                      </Typography>
                    )}
                  </List>
                </CardContent>

                <Box sx={{ p: 3, pt: 0, mt: 'auto' }}>
                  <Button
                    fullWidth
                    variant="contained"
                    disabled={!!isActive || processingId === plan.id}
                    startIcon={processingId === plan.id ? <CircularProgress size={18} /> : <Payment />}
                    onClick={() => handleSubscribe(plan)}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 2,
                      fontWeight: 600,
                      bgcolor: meta.color,
                      color: '#ffffff',
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: alpha(meta.color, 0.9),
                        boxShadow: 2
                      },
                      '&.Mui-disabled': {
                        bgcolor: alpha('#000000', 0.12),
                        color: alpha('#000000', 0.38)
                      }
                    }}
                  >
                    {isActive ? 'Current Plan' : processingId === plan.id ? 'Processing...' : 'Subscribe'}
                  </Button>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );

  const renderBenefits = () => (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: 'text.primary' }}>
        Membership Benefits
      </Typography>
      <Grid container spacing={3}>
        {[
          { icon: <Discount />, title: 'Exclusive Discounts', description: 'Members get special discounts and promotions on products and services.' },
          { icon: <LocalShipping />, title: 'Free Shipping', description: 'Enjoy free delivery on eligible orders and save on shipping costs.' },
          { icon: <NewReleases />, title: 'Early Access', description: 'Be the first to try new products and services as they launch.' },
          { icon: <Person />, title: 'Personalized Recommendations', description: 'Get product suggestions tailored to your needs and preferences.' },
          { icon: <Star />, title: 'Loyalty Rewards', description: 'Earn cashback and rewards on every purchase you make.' },
          { icon: <School />, title: 'Health Resources', description: 'Access blogs, videos, and tips to stay informed about your health.' },
          { icon: <CardMembership />, title: 'Expert Consultations', description: 'Connect with medical professionals for advice and guidance.' },
          { icon: <Groups />, title: 'Community Access', description: 'Join forums and connect with other health-conscious members.' }
        ].map((benefit, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              elevation={1}
              sx={{
                height: '100%',
                borderRadius: 3,
                bgcolor: '#ffffff',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', mr: 2 }}>
                    {benefit.icon}
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {benefit.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {benefit.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderSuccessDialog = () => (
    <Dialog open={successDialog.open} onClose={() => setSuccessDialog({ open: false, membership: null })} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
        <Avatar sx={{ bgcolor: 'success.main', width: 64, height: 64, mx: 'auto', mb: 2 }}>
          <Verified sx={{ color: '#ffffff', fontSize: 36 }} />
        </Avatar>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.dark' }}>
          Welcome to {successDialog.membership?.membership_name}!
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
          Your membership is active until{' '}
          {successDialog.membership?.end_date
            ? new Date(successDialog.membership.end_date).toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })
            : 'N/A'}
          . Start enjoying your exclusive benefits today.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button
          variant="contained"
          onClick={() => setSuccessDialog({ open: false, membership: null })}
          sx={{ textTransform: 'none', borderRadius: 2, px: 4 }}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <SecuredLayout activeTab={4} pageTitle="Membership" contentCols={10}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {renderHero()}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            {renderActiveMembership()}
            {renderPlans()}
            {renderBenefits()}
          </>
        )}

        {renderSuccessDialog()}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ borderRadius: 2 }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </SecuredLayout>
  );
};

export default Membership;
